/*
 * CHATLAUNCHER COMPONENT
 *
 * MANAGES THE CHAT LAUNCHER UI: THE INITIAL SUGGESTION BUBBLES, OPEN/CLOSE BUTTON,
 * AND CONDITIONAL RENDERING OF THE ChatWindow. HANDLES PERSISTENCE CHECKS FOR PRIOR CHAT HISTORY.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import ChatWindow from "../ChatWindow/ChatWindow";
import BubbleButtons from "./BubbleButtons";
import styles from "./ChatLauncher.module.scss";
import { STATIC_REPLIES } from "./staticReplies";

export default function ChatLauncher({ viewport, host }) {
  // Ref to the full launcher container so we can measure its scrollHeight
  const launcherRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [showBubbles, setShowBubbles] = useState(true);
  const [initialMessage, setInitialMessage] = useState("");

  /**
   * ON MOUNT: CHECK SESSION STORAGE FOR EXISTING chatMessages.
   * IF THERE ARE MORE THAN ONE (DEFAULT GREETING + USER MESSAGE),
   * HIDE THE LAUNCHER BUBBLES IMMEDIATELY.
   */
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("chatMessages");
      if (stored) {
        const chatMessages = JSON.parse(stored);
        if (chatMessages.length > 1) {
          setShowBubbles(false);
        }
      }
    } catch (err) {
      console.warn(
        "ChatLauncher: could not read chatMessages from storage",
        err,
      );
    }
  }, []);

  useEffect(() => {
    window.parent.postMessage({ type: "chat:toggle", open: isOpen }, "*");
  }, [isOpen]);

  // 2) Notify parent when bubbles show/hide
  useEffect(() => {
    window.parent.postMessage({ type: "chat:bubbles", show: showBubbles }, "*");
  }, [showBubbles]); /**
   * NEW: Whenever we open/close the chat or show/hide the bubbles,
   * measure the launcher’s total scrollHeight (including absolute children)
   * and post it to the parent. A short timeout lets React/CSS finish rendering
   * before we measure.
   */
  useEffect(() => {
    const t = setTimeout(() => {
      const h = document.documentElement.scrollHeight;
      console.log("📏 [iframe] sending resize →", h);
      window.parent.postMessage(
        { type: "chat:resize", height: Math.ceil(h) },
        "*",
      );
    }, 50);
    return () => clearTimeout(t);
  }, [isOpen, showBubbles]);

  // 3) Notifications for slidein (you probably already have this in Slidein.jsx)
  //    If not, add inside Slidein.toggleSlide:
  //    window.parent.postMessage({type:'chat:slidein',open:next},'*')

  // TOGGLE THE CHAT WINDOW
  const handleToggle = () => {
    if (!isOpen) setShowBubbles(false);
    setIsOpen((open) => !open);
  };

  // BUBBLE CLICK
  const handleBubbleClick = (msg) => {
    setInitialMessage(msg);
    setShowBubbles(false);
    setIsOpen(true);
  };

  const handleCloseBubbles = () => setShowBubbles(false);

  const clearInitialMessage = useCallback(() => {
    setInitialMessage("");
  }, []);

  return (
    <div ref={launcherRef} className={styles.chatLauncherContainer}>
      {viewport === "desktop" && !isOpen && showBubbles && (
        <BubbleButtons
          onBubbleClick={handleBubbleClick}
          onCloseBubbles={handleCloseBubbles}
        />
      )}

      {!isOpen && (
        <button className={styles.launcherButton} onClick={handleToggle}>
          <img
            src="/assets/circle.svg"
            alt="Open chat"
            className={styles.circle}
          />
          <img
            src="/assets/vector-i-circle.svg"
            alt="Info"
            className={styles.vector}
          />
        </button>
      )}

      {isOpen && (
        <div className={styles.chatWindowWrapper}>
          <ChatWindow
            host={host}
            viewport={viewport}
            onClose={handleToggle}
            initialMessage={initialMessage}
            clearInitialMessage={clearInitialMessage}
            staticReplies={STATIC_REPLIES}
          />
        </div>
      )}
    </div>
  );
}
